import { TWasmId } from "~/interfaces";
import { IStoreItem, IWasmStoreGeometry, IWasmStoreMarker } from "./store/interfaces";
import Wasm from "./wasm";
import WasmMapMarker from "./entities/wasm-map-marker";
import WasmMapGeometry from "./entities/wasm-map-geometry";
import { getRemovedIds } from "./utils";
import { watchMapContentChanges } from "~/state/map-content/utils";
import { callClickEvent, callHoverEvent, TStoreEventHoverType } from "./store/events";
import { store } from "~/state/store";
import WasmMapMarkerSharedPosition from "./entities/wasm-map-marker-shared-position";

export default class WasmStoreMapping {
	_wasm: Wasm;

	_markerStoreIdMapping: {
		// klid data id => zmena + obj
		[key: IStoreItem["id"]]: {
			marker: WasmMapMarker | WasmMapMarkerSharedPosition;
			data: IWasmStoreMarker;
			wasmIds: Array<TWasmId>;
		};
	};

	_geometryStoreIdMapping: {
		// klid data id => zmena + obj
		[key: IStoreItem["id"]]: {
			geometry: WasmMapGeometry;
			data: IWasmStoreGeometry;
			wasmIds: Array<TWasmId>;
		};
	};

	// klic data id z wasm jako cislo -> data id str
	// wasm -> store, sdilene pro markery + geometrie
	_wasmToStoreMapping: {
		[key: TWasmId]: IStoreItem["id"];
	};

	constructor(wasm: Wasm) {
		this._wasm = wasm;
		this._markerStoreIdMapping = {};
		this._geometryStoreIdMapping = {};
		this._wasmToStoreMapping = {};

		this._build();
	}

	/**
	 * Mapovani wasm entity id na store id
	 */
	getWasmToStoreId(wasmId: TWasmId) {
		return this._wasmToStoreMapping[wasmId] || 0;
	}

	/**
	 * Mapovani store id entity na wasm ids
	 */
	getStoreToWasmIds(storeId: IStoreItem["id"]): Array<TWasmId> {
		if (storeId in this._markerStoreIdMapping) {
			return this._markerStoreIdMapping[storeId].wasmIds;
		} else if (storeId in this._geometryStoreIdMapping) {
			return this._geometryStoreIdMapping[storeId].wasmIds;
		}

		return [];
	}

	propagateClickEvent(wasmId: TWasmId) {
		const storeId = this.getWasmToStoreId(wasmId);

		if (storeId) {
			callClickEvent(storeId);
		}
	}

	propagateHoverEvent(wasmId: TWasmId, hoverType: TStoreEventHoverType) {
		const storeId = this.getWasmToStoreId(wasmId);

		if (storeId) {
			callHoverEvent(storeId, hoverType);
		}
	}

	_build() {
		// init stav
		const curState = store.getState().mapContent;
		const initMarkers = curState.markers;
		const initGeometries = curState.geometries;

		if (initMarkers.length > 0) {
			this._markersChange(initMarkers.map(item => item.id), initMarkers);
		}

		if (initGeometries.length > 0) {
			this._geometriesChange(initGeometries.map(item => item.id), initGeometries);
		}

		// zmeny stavu
		watchMapContentChanges({
			store,
			onMarkersChange: (ids, markers) => {
				this._markersChange(ids, markers);
			},
			onGeometriesChange: (ids, geometries) => {
				this._geometriesChange(ids, geometries);
			},
		});
	}

	_addMarker(markerData: IWasmStoreMarker) {
		const wasmMarker = this._wasm.createMarker(markerData);
		const wasmIds = wasmMarker.wasmIds;

		this._markerStoreIdMapping[markerData.id] = {
			data: structuredClone(markerData),
			marker: wasmMarker,
			wasmIds,
		};

		for (const wasmId of wasmIds) {
			this._wasmToStoreMapping[wasmId] = markerData.id;
		}
	}

	_removeMarker(markerId: IStoreItem["id"]) {
		if (markerId in this._markerStoreIdMapping) {
			const curData = this._markerStoreIdMapping[markerId];
			const wasmIds = curData.marker.wasmIds;

			curData.marker.destroy();

			for (const wasmId of wasmIds) {
				delete this._wasmToStoreMapping[wasmId];
			}

			delete this._markerStoreIdMapping[markerId];
		}
	}

	_markersChange(affectedIds: Array<IStoreItem["id"]>, markers: Array<IWasmStoreMarker>) {
		const newIds: Array<IStoreItem["id"]> = [];

		// pridani + editace
		for (const marker of markers) {
			newIds.push(marker.id);

			// preskocime
			if (affectedIds.length > 0 && !affectedIds.includes(marker.id)) {
				continue;
			}

			if (marker.id in this._markerStoreIdMapping) {
				const curData = this._markerStoreIdMapping[marker.id];

				if (curData.data.lastChange !== marker.lastChange) {
					if (curData.data.lon !== marker.lon || curData.data.lat !== marker.lat) {
						curData.marker.setPosition(marker.lon, marker.lat);
					}

					if (curData.data.img !== marker.img) {
						curData.marker.setIcon(marker.img);
					}

					if (curData.data.visual !== marker.visual) {
						curData.marker.setVisual(marker.visual);
					}

					if (curData.data.visible !== marker.visible) {
						curData.marker.setVisibility(marker.visible);
					}

					if (curData.data.angle !== marker.angle) {
						curData.marker.setAngle(marker.angle);
					}

					// aktualizujeme
					curData.data = structuredClone(marker);
				}
			} else {
				// neexistuje
				this._addMarker(marker);
			}
		}

		// mazani
		const allIds = Object.keys(this._markerStoreIdMapping).map(item => parseFloat(item));
		const removed = getRemovedIds(allIds, newIds);

		for (const removeId of removed) {
			this._removeMarker(removeId);
		}
	}

	_addGeometry(geomData: IWasmStoreGeometry) {
		const wasmGeometry = this._wasm.createGeometry(geomData);
		const wasmIds = wasmGeometry.wasmIds;

		this._geometryStoreIdMapping[geomData.id] = {
			data: structuredClone(geomData),
			geometry: wasmGeometry,
			wasmIds,
		};

		for (const wasmId of wasmIds) {
			this._wasmToStoreMapping[wasmId] = geomData.id;
		}
	}

	_removeGeometry(geomId: IStoreItem["id"]) {
		if (geomId in this._geometryStoreIdMapping) {
			const curData = this._geometryStoreIdMapping[geomId];
			const wasmIds = curData.geometry.wasmIds;

			curData.geometry.destroy();

			for (const wasmId of wasmIds) {
				delete this._wasmToStoreMapping[wasmId];
			}

			delete this._geometryStoreIdMapping[geomId];
		}
	}

	_geometriesChange(affectedIds: Array<IStoreItem["id"]>, geometries: Array<IWasmStoreGeometry>) {
		const newIds: Array<IStoreItem["id"]> = [];

		// pridani + editace
		for (const geometry of geometries) {
			newIds.push(geometry.id);

			// preskocime
			if (affectedIds.length > 0 && !affectedIds.includes(geometry.id)) {
				continue;
			}

			if (geometry.id in this._geometryStoreIdMapping) {
				const curData = this._geometryStoreIdMapping[geometry.id];

				if (curData.data.lastChange !== geometry.lastChange) {
					if (curData.data.lastGeomChange !== geometry.lastGeomChange) {
						curData.geometry.updatePoints(geometry.points);
						curData.geometry.updateStrPoints(geometry.strPoints);
					}

					if (curData.data.visual !== geometry.visual) {
						curData.geometry.setVisual(geometry.visual);
					}

					if (curData.data.visible !== geometry.visible) {
						curData.geometry.setVisibility(geometry.visible);
					}

					// aktualizujeme
					curData.data = structuredClone(geometry);
				}
			} else {
				// neexistuje
				this._addGeometry(geometry);
			}
		}

		// mazani
		const allIds = Object.keys(this._geometryStoreIdMapping).map(item => parseFloat(item));
		const removed = getRemovedIds(allIds, newIds);

		for (const removeId of removed) {
			this._removeGeometry(removeId);
		}
	}
}
